import produce from 'immer';
import {isEqual} from 'underscore';

import {assertAndContinue} from 'Asserts';
import {
  getStructuredTableNoteParts,
  isStructuredTableNotePart,
} from 'notebook/notes/groups/util/assertIsStructuredTableNotePart';
import {
  TableValidationCellError,
  TableValidationResponse,
} from 'notebook/structured-tables/StructuredTableApiConstants';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {NoteLinkedStructuredTable} from 'notebook/structured-tables/StructuredTablePlugin';
import {ValidationErrorSnapshot} from 'notebook/tables/TableStoreTransforms';
import {BioentityNote, EntryNote, Note} from 'notebook/util/NoteUtilConstants';

import {EvaluatedTable, OutputTableMap} from './Evaluator';

export function getUpdatedNoteWithValidationErrors<T extends BioentityNote | EntryNote>(
  note: T,
  validationErrorResponse: TableValidationResponse
): T {
  if (!validationErrorResponse) {
    return note;
  }
  const {tableName, tableErrors} = validationErrorResponse;
  const previousTableErrors = note.noteRuntimeSnapshot.validationErrorSnapshot?.[tableName] ?? [];
  const notePart = note.noteParts.find(
    (notePart) => isStructuredTableNotePart(notePart) && notePart.item.name === tableName
  );
  assertAndContinue(Boolean(notePart), `Could not find table when processing validation errors`);
  if (!notePart) {
    return note;
  }
  const structuredTable = notePart.item as UnknownStructuredTable;
  const startIndex = validationErrorResponse.startIndex ?? 0;
  const endIndexExclusive = validationErrorResponse.endIndex ?? structuredTable.rows.length;
  const includedRowIds = new Set(
    structuredTable.rows.slice(startIndex, endIndexExclusive).map((row) => row.rowId)
  );
  const updatedErrors = previousTableErrors.filter((error) => !includedRowIds.has(error.rowId));
  updatedErrors.push(...tableErrors);
  return produce(note, (draft: T) => {
    draft.noteRuntimeSnapshot.validationErrorSnapshot = {
      ...draft.noteRuntimeSnapshot.validationErrorSnapshot,
      [tableName]: updatedErrors,
    };
  });
}

/**
 * Our current architecture does not provide entrypoints for each document mutation to be listened to for side
 * effects such as how they might alter the validation state of the document. Consequently, when the document
 * is mutated, we need to manually inspect the old and new document states to determine how the validation
 * snapshot needs to be updated.
 *
 * 1. Did an evaluated value change, making some validation errors stale? These need to be removed.
 * 2. Did a table name change, making the keys in the validation snapshot stale? These need to be updated.
 */
export function getDocumentWithUpdatedValidationSnapshot<T>({
  oldDocument,
  newDocument,
  getNote,
  skipValidationStalenessCheck,
}: {
  oldDocument?: T;
  newDocument: T;
  getNote: (document: T) => Note;
  skipValidationStalenessCheck: boolean;
}): T {
  if (oldDocument == null || skipValidationStalenessCheck) {
    return newDocument;
  }

  const updated = getNoteWithUpdatedValidationSnapshot({
    oldNote: getNote(oldDocument),
    newNote: getNote(newDocument),
  });

  return produce(newDocument, (draft: T) => {
    Object.assign(getNote(draft), updated);
  });
}

function getNoteWithUpdatedValidationSnapshot({oldNote, newNote}: {oldNote: Note; newNote: Note}): Note {
  const {validationErrorSnapshot} = newNote.noteRuntimeSnapshot;
  if (validationErrorSnapshot == null) {
    return newNote;
  }

  const oldTables = getStructuredTableNoteParts(oldNote.noteParts);
  const newTables = getStructuredTableNoteParts(newNote.noteParts);
  const oldEvaluationSnapshot = oldNote.noteRuntimeSnapshot.evaluationSnapshot;
  const newEvaluationSnapshot = newNote.noteRuntimeSnapshot.evaluationSnapshot;

  let updated = getValidationSnapshotWithoutStaleValidationErrors({
    validationErrorSnapshot,
    oldTables,
    newTables,
    oldEvaluationSnapshot,
    newEvaluationSnapshot,
  });

  updated = getValidationSnapshotWithTableRenamed({
    validationErrorSnapshot: updated,
    oldTables,
    newTables,
  });

  return produce(newNote, (draft) => {
    draft.noteRuntimeSnapshot.validationErrorSnapshot = updated;
  });
}

function getValidationSnapshotWithTableRenamed({
  validationErrorSnapshot,
  oldTables,
  newTables,
}: {
  validationErrorSnapshot: ValidationErrorSnapshot;
  oldTables: Array<NoteLinkedStructuredTable>;
  newTables: Array<NoteLinkedStructuredTable>;
}): ValidationErrorSnapshot {
  const updated: ValidationErrorSnapshot = {...validationErrorSnapshot};
  for (const [tableName, validationErrors] of Object.entries(validationErrorSnapshot)) {
    const oldTable = oldTables.find((t) => t.name === tableName);
    if (oldTable == null) {
      continue;
    }
    const newTable = newTables.find((t) => t.provenanceUuid === oldTable.provenanceUuid);
    if (newTable == null) {
      continue;
    }

    delete updated[tableName];
    updated[newTable.name] = validationErrors;
  }

  return updated;
}

function getValidationSnapshotWithoutStaleValidationErrors({
  validationErrorSnapshot,
  oldTables,
  newTables,
  oldEvaluationSnapshot,
  newEvaluationSnapshot,
}: {
  validationErrorSnapshot: ValidationErrorSnapshot;
  oldTables: Array<NoteLinkedStructuredTable>;
  newTables: Array<NoteLinkedStructuredTable>;
  oldEvaluationSnapshot: OutputTableMap | null;
  newEvaluationSnapshot: OutputTableMap | null;
}): ValidationErrorSnapshot {
  const updated: ValidationErrorSnapshot = {...validationErrorSnapshot};
  for (const [tableName, validationErrors] of Object.entries(validationErrorSnapshot)) {
    const newTable = newTables.find((t) => t.name === tableName);
    if (newTable == null) {
      continue;
    }
    const oldTable = oldTables.find((t) => t.provenanceUuid === newTable.provenanceUuid);
    if (oldTable == null) {
      continue;
    }
    const newEvaluation = newEvaluationSnapshot?.[tableName];
    if (newEvaluation == null) {
      continue;
    }
    const oldEvaluation = oldEvaluationSnapshot?.[tableName];
    if (oldEvaluation == null) {
      continue;
    }
    updated[tableName] = getUpdatedTableWithoutStaleValidationErrors({
      oldTable,
      oldEvaluation,
      newTable,
      newEvaluation,
      validationErrors,
    });
  }

  return updated;
}

function getUpdatedTableWithoutStaleValidationErrors({
  oldTable,
  oldEvaluation,
  newTable,
  newEvaluation,
  validationErrors,
}: {
  oldTable: NoteLinkedStructuredTable;
  oldEvaluation: EvaluatedTable;
  newTable: NoteLinkedStructuredTable;
  newEvaluation: EvaluatedTable;
  validationErrors: Array<TableValidationCellError>;
}): Array<TableValidationCellError> {
  const {colIndices: oldTableColIndices, rowIndices: oldTableRowIndices} = getEvaluatedCellLookup(oldTable);
  const {colIndices: newTableColIndices, rowIndices: newTableRowIndices} = getEvaluatedCellLookup(newTable);

  // Get stale validation errors
  const staleValidationErrors = new Set<string>();
  for (const {columnProvenanceUuid, rowId, text} of validationErrors) {
    // The table may have been updated by adding, removing or reordering rows/columns, so we need to locate
    // the cell in each evaluated table by their IDs
    const oldRowIndex = oldTableRowIndices[rowId];
    const newRowIndex = newTableRowIndices[rowId];
    const oldColIndex = oldTableColIndices[columnProvenanceUuid];
    const newColIndex = newTableColIndices[columnProvenanceUuid];
    const oldValue = oldEvaluation[oldRowIndex]?.[oldColIndex];
    const newValue = newEvaluation[newRowIndex]?.[newColIndex];

    if (!isEqual(oldValue?.value, newValue?.value)) {
      // eslint-disable-next-line camelcase
      for (const {error_uuid} of text) {
        staleValidationErrors.add(error_uuid);
      }
    }
  }

  // Remove all stale validation errors
  const updatedTableValidationErrors: Array<TableValidationCellError> = [];
  for (const error of validationErrors) {
    // eslint-disable-next-line camelcase
    const text = error.text.filter(({error_uuid}) => !staleValidationErrors.has(error_uuid));
    if (text.length > 0) {
      updatedTableValidationErrors.push({...error, text});
    }
  }
  return updatedTableValidationErrors;
}

function getEvaluatedCellLookup(structuredTable: UnknownStructuredTable): {
  colIndices: Record<string, number>;
  rowIndices: Record<string, number>;
} {
  return {
    colIndices: Object.fromEntries(structuredTable.columns.map((c, i) => [c.provenanceUuid, i])),
    rowIndices: Object.fromEntries(structuredTable.rows.map((r, i) => [r.rowId, i])),
  };
}
